import React from "react";
import { Target, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDashTheme } from "../../hooks/useDashTheme";
import { Drawer } from "../../components/Drawer";
import { Button, Badge, Avatar } from "../../components/primitives";
import { DataTable, EmptyState } from "../../components/DataTable";
import { getGoalsByProject } from "../../../../services/goalsPerformanceApi";
import { DASHBOARD_BASE } from "../../../../config/featureFlags";

const goalStatusTone = (s) => (s === "APPROVED" ? "positive" : s === "PENDING" ? "warning" : "neutral");

// Slide-in goals list for one project. Opened from the Goals count on the
// projects list so you can read a project's goals without leaving the list.
export function ProjectGoalsPanel({ project, onClose }) {
  const t = useDashTheme();
  const navigate = useNavigate();
  const [goals, setGoals] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const projectId = project ? project._id || project.id : null;

  React.useEffect(() => {
    if (!projectId) return;
    let alive = true;
    setLoading(true);
    setGoals([]);
    (async () => {
      try {
        const res = await getGoalsByProject(projectId);
        if (alive && res.success) setGoals(res.data?.goals || []);
      } catch (_) {
        if (alive) setGoals([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [projectId]);

  const totalWeight = goals.reduce((sum, g) => sum + Number(g.weightage || g.weight || 0), 0);

  return (
    <Drawer
      open={!!project}
      onClose={onClose}
      title={project?.name || "Goals"}
      subtitle={loading ? "Loading goals…" : `${goals.length} goal${goals.length === 1 ? "" : "s"} · ${totalWeight}% total weight`}
      icon={<Target size={18} />}
      width={1040}
      footer={
        <>
          <Button variant="secondary" leftIcon={<ExternalLink size={14} />}
            onClick={() => navigate(`${DASHBOARD_BASE}/projects/${projectId}`, { state: { project } })}>
            Open project
          </Button>
          <Button variant="primary" onClick={onClose}>Close</Button>
        </>
      }
    >
      <DataTable
        loading={loading}
        skeletonRows={6}
        rows={goals}
        rowKey={(g, i) => g._id ?? i}
        emptyState={<EmptyState icon={<Target size={22} />} title="No goals yet"
          description="No goals have been assigned to this project." />}
        columns={[
          {
            key: "name", header: "Goal",
            render: (g) => (
              <div style={{ minWidth: 0, maxWidth: 320 }}>
                <div style={{ fontSize: t.font.size.md, fontWeight: 500, color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.name}</div>
                {g.description && <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.description}</div>}
              </div>
            ),
          },
          {
            key: "assignee", header: "Assigned to", width: 190,
            render: (g) => {
              const name = g.userId ? `${g.userId.firstName || ""} ${g.userId.lastName || ""}`.trim() || "Unknown" : "Unknown";
              return (
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <Avatar user={g.userId} name={name} size={24} />
                  <span style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
                </div>
              );
            },
          },
          { key: "category", header: "Category", width: 130, render: (g) => <Badge tone="neutral">{g.category || "—"}</Badge> },
          { key: "weight", header: "Weight", align: "right", width: 90, render: (g) => <span style={{ fontWeight: 600, color: t.c.text, fontVariantNumeric: "tabular-nums" }}>{g.weightage || g.weight || 0}%</span> },
          { key: "achieved", header: "Achieved", align: "right", width: 100, render: (g) => (
            <span style={{ color: g.achievedPercent == null ? t.c.textTertiary : t.c.text, fontVariantNumeric: "tabular-nums" }}>
              {g.achievedPercent == null ? "—" : `${g.achievedPercent}%`}
            </span>
          ) },
          { key: "status", header: "Status", width: 120, render: (g) => <Badge tone={goalStatusTone(g.status)}>{g.status || "—"}</Badge> },
          { key: "cycle", header: "Cycle", width: 120, render: (g) => (
            <span style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, whiteSpace: "nowrap" }}>
              {g.cycleId ? `${g.cycleId.month}/${g.cycleId.year}` : "—"}
            </span>
          ) },
        ]}
      />
    </Drawer>
  );
}
